import { parseDescription } from '@theme/pod-spec-parser';

/**
 * <pod-specifications> - renders the universal POD Specifications/Measurements component from a
 * product's raw description HTML (read from its own child <script type="application/json">, never
 * from innerHTML of untrusted markup). Presentation-only: all parsing/normalization lives in
 * pod-spec-parser.js and this file never special-cases Printful/Printify/Tapstitch markup shapes.
 *
 * Safety: the only untrusted input is the description string. It is parsed into plain data
 * (strings/numbers) by pod-spec-parser.js, and every value rendered here goes through
 * `textContent`/`createElement`, never `innerHTML` - supplier HTML (including any inline <script>)
 * is never executed or copied into the live DOM.
 */
class PodSpecifications extends HTMLElement {
  connectedCallback() {
    if (this.querySelector('.pod-specs')) return; // already rendered (e.g. re-upgraded)

    const dataScript = this.querySelector('script[type="application/json"]');
    if (!dataScript) return;

    let description = '';
    try {
      description = JSON.parse(dataScript.textContent) || '';
    } catch {
      return;
    }

    let spec;
    try {
      spec = parseDescription(description);
    } catch (err) {
      console.warn('[pod-specifications] parser error, nothing rendered:', err);
      return;
    }

    if (spec.warnings && spec.warnings.length) {
      console.warn('[pod-specifications] parser warnings:', spec.warnings);
    }

    // Owner correction (PDP tabs restructuring task): "size guide" is now its own PDP tab, separate
    // from "object" - this one <pod-specifications> instance renders ONLY the measurements section
    // (data-pod-specs-mode="measurements", set by snippets/pod-specifications.liquid's own
    // only_measurements param, used by the "size guide" tab's render call) or everything EXCEPT it
    // (the default mode, used by "object"'s own render call) - never both, so the same measurement
    // data never renders twice across the two tabs. Both instances parse the same description
    // independently (no shared state between them) - simplest correct option per this task's own
    // "no new/unnecessary abstractions" instruction, and parsing is a pure, inexpensive, client-side
    // operation with no side effects either way.
    if (this.dataset.podSpecsMode === 'measurements') {
      const hasMeasurementsContent = (spec.measurements && spec.measurements.metrics.length) || spec.sizingNotes.length;
      if (!hasMeasurementsContent) return;
      const root = document.createElement('div');
      root.className = 'pod-specs';
      // Owner addition (PDP tabs design prototype task): "size guide"'s own unframed intro
      // paragraph - real sizing notes when the parser found any (e.g. "order a size down"),
      // otherwise a generic, non-product-specific fallback sentence (PROTOTYPE COPY, not verified
      // per-product guidance - see appendSizeGuideIntro below). The measurements frame itself no
      // longer carries sizing notes or its own heading (moved here, or removed - see
      // buildMeasurements below).
      appendSizeGuideIntro(root, spec.sizingNotes);
      root.appendChild(this.buildMeasurements(spec.measurements));
      this.appendChild(root);
      return;
    }

    // Owner correction (PDP safety and compliance task): the previous early return on empty
    // details/care/productSafety/descriptionIntro no longer applies - buildRoot below now always
    // renders the "product safety contact" and "store operator" subsections regardless of what the
    // supplier's own description contains, so this instance is never genuinely empty anymore.
    // unclassifiedContent itself is still intentionally excluded from ever being rendered: it is
    // almost always generic supplier marketing prose (e.g. "Upgrade your wardrobe with...") whose
    // real facts are already captured separately via details/care - Ukrainciaga supplies its own
    // editorial intro (product-info-accordion.liquid's subject_matter param, or - when that
    // metafield is blank - spec.descriptionIntro, a structurally-detected leading prose block; see
    // buildRoot/hasMetafieldIntro below) instead of showing supplier prose verbatim. The data itself
    // is not discarded - see the console.info below - only not rendered.
    if (spec.unclassifiedContent.length) {
      console.info('[pod-specifications] unclassified content (not rendered, likely marketing prose):', spec.unclassifiedContent);
    }

    this.appendChild(this.buildRoot(spec));
  }

  /** @param {import('@theme/pod-spec-parser').NormalizedSpec} spec */
  buildRoot(spec) {
    const root = document.createElement('div');
    root.className = 'pod-specs';

    // Owner correction (Specifications layout correction task): restored - a product with no
    // custom.intent metafield (Subject matter blank) still gets a technical intro paragraph here,
    // sourced from product.description itself (never custom.intent) - only its visible "intro:"
    // heading is gone now, not the content. Only when no custom.intent metafield intro was already
    // rendered as a sibling (product-info-accordion.liquid/pod-specifications.liquid) - metafield
    // always wins, never both at once.
    if (spec.descriptionIntro && !this.hasMetafieldIntro()) {
      appendIntroSection(root, spec.descriptionIntro);
    }

    // Owner correction (Specifications final layout task): "product details:" and "care
    // instructions:" are now two separate sections (were one merged "details and care" section) -
    // each with its own exact heading, same plain one-line-per-item presentation. No semantic
    // key/value re-expression in this presentation (see pod-spec-parser.js's
    // classifySemanticFields/specFields - still computed, just not rendered here).
    appendDetailsSection(root, spec.details);
    appendCareSection(root, spec.care);

    // Owner correction (PDP tabs restructuring task): measurements (sizing notes + toggle + table)
    // moved out entirely - "size guide" is now its own PDP tab, rendered by a separate
    // <pod-specifications data-pod-specs-mode="measurements"> instance (see connectedCallback
    // above), never here.
    //
    // Owner correction (PDP safety and compliance restructure task): the two separate "product
    // safety contact"/"store operator" subsections are gone - appendSafetySection below now builds
    // everything (GPSR fields + the global GPSR statement + store operator) as ONE compact
    // subsection with no internal dividers between them.
    appendSafetySection(root, spec.productSafety, spec.unverifiedGpsrCandidates, this.metafieldManufacturer);

    return root;
  }

  /** True when a metafield-sourced intro (product-info-accordion.liquid's subject_matter param)
   * was already rendered as this element's preceding sibling inside .pod-specs-root. */
  hasMetafieldIntro() {
    const parent = this.parentElement;
    return Boolean(parent && parent.querySelector(':scope > .pod-specs__intro'));
  }

  /**
   * GPSR production safety task (merchant-editable manufacturer source of truth): composes the
   * three data attributes snippets/pod-specifications.liquid renders from the resolved metafield
   * values (product override, else shop default - resolution happens in Liquid, this just reads
   * the result) into the single "name, address, email" string buildGpsrFields expects. Blank
   * values are dropped, never rendered as an empty "," segment. Returns null when nothing was
   * configured at all - never fabricated, never defaulted.
   * @returns {string|null}
   */
  get metafieldManufacturer() {
    const name = (this.dataset.manufacturerName || '').trim();
    const address = (this.dataset.manufacturerAddress || '').trim();
    const email = (this.dataset.manufacturerEmail || '').trim();
    const composed = [name, address, email].filter(Boolean).join(', ');
    return composed || null;
  }

  /**
   * Owner correction (PDP tabs design prototype task): no heading, no sizing notes (moved to
   * "size guide"'s own unframed intro, see connectedCallback/appendSizeGuideIntro) - just the
   * toggle + table, now one bordered frame (pod-specs__section, styled in blocks/product-info-
   * accordion.liquid alongside every other tab's frames).
   * @param {import('@theme/pod-spec-parser').MeasurementTable|null} table
   */
  buildMeasurements(table) {
    const section = document.createElement('div');
    section.className = 'pod-specs__measurements pod-specs__section';

    if (!table || !table.metrics.length) return section;

    // Owner correction (Specifications layout correction task): exposed here too (not just on the
    // <table> element below) so the cm/inch toggle's own CSS can size itself to exactly match the
    // table's own data-column width/position - custom properties cascade down to descendants, and
    // this section element is the nearest shared ancestor of both the toggle and the table. Owner
    // redesign (Editorial Dotted Matrix task): sizes are now the table's columns (table.metrics are
    // the rows) - this counts the SIZE columns only; the row-label column is a separate `auto`
    // grid track (see .pod-specs__table's own grid-template-columns), not part of this count. Owner
    // correction (PDP remove artificial size guide data task): back to this product's own real
    // table.sizes.length - the temporary fixed 8-column (S-5XL) preview is retired entirely, per
    // this task's own explicit "restore dynamic rendering of exclusively genuine, product-specific
    // sizes" instruction.
    section.style.setProperty('--pod-specs-table-columns', String(table.sizes.length));

    // Default unit: cm. No reliable native store/market "unit system" preference exists to read
    // here (Shopify exposes measurement units for shipping/weight, not a customer-facing display-
    // unit setting), and all three suppliers' own source data is primarily cm-based - a documented,
    // consistent default per the task's own fallback instruction, not a guess from browser/locale.
    const unitState = { unit: /** @type {'cm'|'in'} */ ('cm') };

    // Owner correction (size guide unit switcher task): the toggle is now its own inner bordered
    // frame above the measurements, matching the main PDP tab navigation's design exactly
    // (.product-tabs__nav/.product-tabs__label, blocks/product-info-accordion.liquid) - no longer
    // the frame's heading-with-divider slot (that treatment is reserved for real section headings).
    const toggle = buildUnitToggle(unitState, () => renderTable());
    section.appendChild(toggle);

    const body = appendFrameBody(section);

    const wrap = document.createElement('div');
    wrap.className = 'pod-specs__measurements-wrap';
    body.appendChild(wrap);

    const renderTable = () => {
      wrap.replaceChildren(buildMeasurementTable(table, unitState.unit));
    };
    renderTable();

    return section;
  }
}

/**
 * Owner redesign (PDP "Editorial Dotted Matrix" task): transposed from the previous "one row per
 * size, one column per metric" shape - sizes are now the COLUMNS, in whichever subset this
 * product's own table.sizes actually contains (owner correction, PDP remove artificial size guide
 * data task: the temporary fixed S-5XL 8-column preview and its 3XL-5XL sample-fill placeholder
 * values are both retired entirely - "restore dynamic rendering of exclusively genuine,
 * product-specific sizes and measurements," never padded with invented sizes a product doesn't
 * have), and each metric (body length/sleeve length/chest/...) is its own ROW, labeled with the
 * parser's own real, verbatim label (table.metrics[].label, unit-suffix already stripped by the
 * parser) - never a hardcoded or shortened substitute, so a product with a metric set different
 * from the typical body/sleeve/chest trio still shows its own real labels. A thin aria-hidden
 * divider row is inserted between metric rows, styled as a full-width dashed line - see
 * .pod-specs__table-divider in snippets/pod-specifications.liquid.
 * @param {import('@theme/pod-spec-parser').MeasurementTable} table
 * @param {'cm'|'in'} unit
 */
function buildMeasurementTable(table, unit) {
  const el = document.createElement('table');
  el.className = 'pod-specs__table';

  // Owner request (2027-01-08): equal-ish column widths via a shared CSS grid template (size guide
  // refinement task) - the actual column count is however many sizes this product's own table has
  // (never hardcoded or padded to a fixed count - see this function's own doc comment). A plain
  // CSS literal couldn't know this per-table, so it is exposed here as a custom property the
  // stylesheet's grid-template-columns reads.
  el.style.setProperty('--pod-specs-table-columns', String(table.sizes.length));

  // Owner request (2026-12-16): numeric cells no longer repeat "cm"/"in" after every value (the
  // selected system is already explicit in the [ centimeters | inches ] toggle) - the caption,
  // already screen-reader-only and already rebuilt on every unit switch (renderTable() below
  // replaces the whole table), is extended to name the active system instead, so that context
  // isn't lost for assistive tech navigating the table on its own.
  const caption = document.createElement('caption');
  caption.className = 'visually-hidden';
  caption.textContent = `Size guide (${unitLabel(unit)})`;
  el.appendChild(caption);

  const thead = document.createElement('thead');
  const headRow = document.createElement('tr');
  // Blank corner cell (row-label column has no size) - sr-only text keeps the table's own 2D
  // header structure announced correctly to assistive tech without showing visible clutter. Not
  // the shared .visually-hidden utility (position: absolute) - that would pull this cell out of
  // the table's own CSS Grid item flow entirely, shifting every real size header one column left
  // out of alignment with the data rows below. .pod-specs__table-corner (next to .visually-hidden
  // in this file's own stylesheet) hides it the same way without leaving grid flow.
  const corner = th('', 'col');
  corner.className = 'pod-specs__table-corner';
  corner.textContent = 'measurement';
  headRow.appendChild(corner);
  for (const size of table.sizes) {
    headRow.appendChild(th(size, 'col'));
  }
  thead.appendChild(headRow);
  el.appendChild(thead);

  const tbody = document.createElement('tbody');
  table.metrics.forEach((metric, index) => {
    if (index > 0) tbody.appendChild(buildDividerRow());

    const row = document.createElement('tr');
    row.appendChild(th(metric.label.toLowerCase(), 'row'));
    for (const size of table.sizes) {
      const value = metric.values.find((v) => v.size === size);
      row.appendChild(buildCell(value, unit));
    }
    tbody.appendChild(row);
  });
  el.appendChild(tbody);

  return el;
}

/** Purely decorative row between two metric rows - see this file's own buildMeasurementTable doc
 * comment for why a dedicated row (not a per-cell border) is needed for a short/inset divider. */
function buildDividerRow() {
  const row = document.createElement('tr');
  row.setAttribute('aria-hidden', 'true');
  const cell = document.createElement('td');
  cell.className = 'pod-specs__table-divider';
  row.appendChild(cell);
  return row;
}

/** @param {string} text @param {'row'|'col'} scope */
function th(text, scope) {
  const cell = document.createElement('th');
  cell.scope = scope;
  cell.textContent = text;
  return cell;
}

/**
 * @param {import('@theme/pod-spec-parser').MeasurementValue|undefined} value
 * @param {'cm'|'in'} unit
 */
function buildCell(value, unit) {
  const cell = document.createElement('td');

  const valueSpan = document.createElement('span');
  valueSpan.className = 'pod-specs__cell-value';

  if (!value) {
    valueSpan.textContent = '—';
  } else {
    const numeric = unit === 'in' ? value.in : value.cm;
    if (numeric === null || numeric === undefined) {
      valueSpan.textContent = value.originalText || '—';
    } else {
      // Owner request (2026-12-16): no per-cell "cm"/"in" suffix - the active unit is already
      // explicit in the toggle above the table. Presentation-only: the underlying value (and its
      // unit metadata) in the normalized data model is untouched.
      valueSpan.textContent = formatMeasurement(numeric, unit);
    }
  }
  cell.appendChild(valueSpan);
  return cell;
}

/**
 * Universal DISPLAY rounding (owner correction, 2027-01-15): every measurement value - centimeters
 * and inches alike - now floors to a whole integer, no decimals at all (replaces the previous
 * nearest-0.5/nearest-0.1 rule, which still left some values showing ".5"). Deliberately
 * conservative/downward rounding, so a garment is never displayed as larger than its real source
 * measurement. Same rule for every provider (Printful/Printify/Tapstitch/any future one), never a
 * provider-specific variant. Presentation-only: this only affects what text is shown in a cell -
 * the normalized numeric value in the parsed data model (value.cm/value.in) is never mutated, and
 * every other consumer of that data (the parser's own tests, the accessible caption, the
 * regression checks) still sees the full-precision source number.
 * @param {number} n @param {'cm'|'in'} unit - kept for call-site stability; both units floor the
 * same way now, so this no longer changes the result.
 */
export function formatMeasurement(n, unit) {
  return String(Math.floor(n));
}

/** The table caption's accessible unit name (visually-hidden, screen readers only) - kept spelled
 * out in full regardless of the toggle's own shortened visible label (below), per the owner's own
 * "change VISIBLE labels only" instruction. @param {'cm'|'in'} unit */
function unitLabel(unit) {
  return unit === 'in' ? dict('pod_specs_unit_in', 'inches') : dict('pod_specs_unit_cm', 'centimeters');
}

/** The toggle button's own visible text (owner request, PDP size guide redesign task:
 * "metric (cm)"/"imperial (inch)" -> "metric: cm"/"imperial: inch") - separate from unitLabel()
 * above, which stays spelled out for the sr-only caption. Not routed through dict()/locale files
 * like every other string here: this project's locale files are validated for matching keys across
 * every supported language (Theme Check's MatchingTranslations rule), so a new key would need
 * adding to all ~30 of them, most with no reliable translation available - and live testing showed
 * this component's dict() lookups don't actually reach window.Theme.translations on either the /en
 * or /uk storefront anyway (both render the JS fallback regardless of locale), so a new translation
 * key would have had no confirmed effect besides the lint errors. @param {'cm'|'in'} unit */
function unitShortLabel(unit) {
  return unit === 'in' ? 'imperial: inch' : 'metric: cm';
}

/**
 * @param {{unit: 'cm'|'in'}} unitState
 * @param {() => void} onChange
 */
function buildUnitToggle(unitState, onChange) {
  const group = document.createElement('div');
  group.className = 'pod-specs__unit-toggle';
  group.setAttribute('role', 'group');
  group.setAttribute('aria-label', dict('pod_specs_unit_toggle_label', 'Measurement units'));

  // Owner request (2026-12-16): [ cm | inch ] order unchanged since - only the visible label text
  // (unitShortLabel, above) has changed over time, never this order. Each entry's own first
  // element (the unit VALUE: 'cm'/'in') is what actually drives buildMeasurementTable's conversion
  // - only the label text changes, never the underlying data mapping (cm -> cm, inch -> in).
  const units = [
    ['cm', unitShortLabel('cm')],
    ['in', unitShortLabel('in')],
  ];

  const buttons = units.map(([value, label]) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pod-specs__unit-button';
    button.textContent = label;
    button.setAttribute('aria-pressed', String(unitState.unit === value));
    button.addEventListener('click', () => {
      if (unitState.unit === value) return;
      unitState.unit = value;
      for (const [btn, [v]] of buttonPairs()) btn.setAttribute('aria-pressed', String(v === value));
      onChange();
    });
    return button;
  });

  function buttonPairs() {
    return buttons.map((b, i) => /** @type {[HTMLButtonElement, [string, string]]} */ ([b, units[i]]));
  }

  for (const button of buttons) group.appendChild(button);
  return group;
}

/** @param {Element} container @param {string} text */
function appendSectionHeading(container, text) {
  const heading = document.createElement('p');
  heading.className = 'pod-specs__section-heading';
  heading.textContent = text;
  container.appendChild(heading);
}

/** Owner correction (Specifications layout correction task): restored (was removed entirely in a
 * prior task) - no heading this time, per that task's own explicit "remove ONLY the visible
 * 'intro:' heading, keep the actual text" instruction. Never shows alongside a metafield-sourced
 * intro (hasMetafieldIntro() above gates the one call site), so this is never "Subject matter"
 * content - it is always sourced from product.description itself.
 * @param {Element} root @param {string} text */
function appendIntroSection(root, text) {
  const intro = document.createElement('div');
  intro.className = 'pod-specs__intro';
  const p = document.createElement('p');
  p.textContent = text;
  intro.appendChild(p);
  root.appendChild(intro);
}

/** Prototype placeholder (PDP tabs design prototype task) - generic, non-product-specific; not a
 * verified per-product claim. Used only when the parser found no genuine sizing notes for this
 * product. Replace with real per-product guidance once available (unresolved for this prototype).
 */
const SIZE_GUIDE_INTRO_FALLBACK = 'refer to the measurements below for your exact size.';

/** @param {Element} root @param {string[]} sizingNotes */
function appendSizeGuideIntro(root, sizingNotes) {
  const intro = document.createElement('div');
  intro.className = 'pod-specs__intro';
  const p = document.createElement('p');
  p.textContent = sizingNotes.length ? sizingNotes.join(' ') : SIZE_GUIDE_INTRO_FALLBACK;
  intro.appendChild(p);
  root.appendChild(intro);
}

/**
 * Builds a plain one-line-per-item list - shared by appendDetailsSection/appendCareSection below
 * (same presentation, different source field/heading/section class).
 * @param {string[]} lines
 */
function buildPlainList(lines) {
  const list = document.createElement('ul');
  list.className = 'pod-specs__list';
  for (const line of lines) {
    const li = document.createElement('li');
    // The text lives in its own child <span>, not as the li's direct text node - confirmed live:
    // assets/digit-letter-spacing.js (sitewide, owner-requested) wraps every digit run anywhere on
    // the page in its own <span class="js-digit-spacing"> via a MutationObserver, which fires on
    // this list too. That splits a bare li.textContent assignment into multiple top-level child
    // nodes (text, span, text, ...) the moment it contains a digit (e.g. "100% organic...", "5.9
    // oz...", "1 x 1 rib...") - a single wrapper span keeps the li's own direct children fixed at
    // exactly one regardless of what the digit-spacing script does inside it afterward.
    const text = document.createElement('span');
    text.textContent = line;
    li.appendChild(text);
    list.appendChild(li);
  }
  return list;
}

/** @param {Element} container @returns {Element} a .pdp-tab-frame__body div, appended and returned
 * for the caller to fill - same wrapper snippets/pdp-tab-frame.liquid uses, so the shared two-column
 * "editorial side heading" CSS (blocks/product-info-accordion.liquid) lays out every section the
 * same way regardless of whether it was built here or by that Liquid snippet. */
function appendFrameBody(container) {
  const body = document.createElement('div');
  body.className = 'pdp-tab-frame__body';
  container.appendChild(body);
  return body;
}

/** Owner correction (PDP cosmetic update task): inner subsection heading renamed "product details"
 * -> "specifications" - the accordion ROW's own heading (blocks/product-info-accordion.liquid) is
 * now also "product details" (PDP accordion refinement task), so the two used to read as a
 * duplicated heading directly inside each other; this is the inner one, left distinct on purpose.
 * row_id/content/data source are unchanged - heading text only.
 * @param {Element} root @param {string[]} lines */
function appendDetailsSection(root, lines) {
  if (!lines || lines.length === 0) return;
  const section = document.createElement('div');
  section.className = 'pod-specs__details pod-specs__section';
  appendSectionHeading(section, 'specifications');
  appendFrameBody(section).appendChild(buildPlainList(lines));
  root.appendChild(section);
}

/** @param {Element} root @param {string[]} lines */
function appendCareSection(root, lines) {
  if (!lines || lines.length === 0) return;
  const section = document.createElement('div');
  section.className = 'pod-specs__care pod-specs__section';
  appendSectionHeading(section, 'care instructions');
  appendFrameBody(section).appendChild(buildPlainList(lines));
  root.appendChild(section);
}

/** Owner addition (PDP safety and compliance task): fixed, non-product-specific EU consumer-law
 * statement - none of this catalog's real supplier text states this (the only "2 year" clause any
 * fixture contains is the OUTDATED Directive 1999/44/EC warranty clause already stripped out of
 * `product identification` above, which this task's owner separately confirmed must never be
 * shown as current legal information). The 2-year legal guarantee is a baseline EU consumer right
 * (Directive (EU) 2019/771) that applies identically to every product sold into the EU regardless
 * of supplier - correctly presented here as a fixed, always-shown statement, not parsed/invented
 * per-product.
 *
 * Owner correction (PDP safety and compliance restructure task): shortened from a full explanatory
 * sentence to the exact approved "2 years" value - the GPSR statement below now carries the legal
 * framing instead, as its own separate paragraph. */
const EU_LEGAL_GUARANTEE_TEXT = '2 years';

/** Owner addition (PDP safety and compliance task): Ukrainciaga's own customer-facing safety
 * contact - distinct from, and never a substitute for, the supplier-sourced EU responsible person
 * below ("must NOT replace or be misrepresented as the legally required EU responsible person" -
 * this task's own explicit instruction). One shared constant, not a per-product metafield - every
 * product shows the identical address. */
const PRODUCT_SAFETY_CONTACT_EMAIL = 'contact@ukrainciaga.com';

/** Owner addition (PDP safety and compliance restructure task): the exact approved GPSR statement
 * and safety-contact sentence, global and identical on every product - never per-product text, and
 * never silently reworded (this task's own explicit "do not silently change the approved copy").
 * Split around the one embedded link so appendGpsrStatementParagraph below can build real text
 * nodes either side of a real <a>, rather than auto-detecting the email the way
 * appendTextWithEmailLinks does (that helper also replaces the visible text with the generic word
 * "email" - correct for supplier-sourced GPSR contact data, but wrong here: this is Ukrainciaga's
 * own address, shown as its real literal text, same as every other Ukrainciaga-owned link in this
 * component). */
// Owner correction (PDP safety-and-compliance layout fix task): "(GPSR)" removed from the opening
// sentence - exact approved wording, never reworded beyond this one explicit instruction.
const GPSR_STATEMENT_PREFIX =
  'in accordance with the general product safety regulation, ukrainciaga ensures that all products offered through our store are safe and meet applicable eu product safety requirements. for any product safety inquiries or concerns, please contact us at ';
const GPSR_STATEMENT_SUFFIX = '.';

/** Owner addition (PDP safety and compliance task): the store operator's own fixed legal identity
 * - one shared constant for every product, never a per-product metafield.
 *
 * Owner correction (PDP safety and compliance restructure task): postal code added ("04050");
 * email removed from this line (the GPSR statement paragraph above already gives the one safety-
 * contact email - repeating it here would be redundant, not a second distinct address).
 *
 * Owner correction (PDP safety-and-compliance inline redesign task): intentionally no longer
 * rendered anywhere in this subsection ("remove the separate store operator display... do not
 * delete its stored data" - this task's own explicit instruction) - kept here, unused, as the one
 * still-current source of this data for whenever it's needed again. */
const STORE_OPERATOR = {
  name: 'Ukrainciaga International',
  address: '6 Alexander Herzen Street, Kyiv, Ukraine, 04050',
  website: 'ukrainciaga.com',
};

// GPSR production safety task (legal-data correctness fix): a small, explicit allowlist of
// externally-verified real POD EU-responsible-person entities - NOT a general trust mechanism, and
// never extended on a guess. Each entry here was independently confirmed outside this codebase
// before being added:
//   - "SINDEN VENTURES LIMITED" (Limassol, Cyprus): confirmed via Printful's own public help
//     documentation, which names this entity as Printful's real EU responsible person for
//     sellers using Printful's white-label fulfillment.
//   - "HONSON VENTURES LIMITED" (Limassol, Cyprus): the only EU representative this catalog has
//     ever had in a confirmed-authentic, explicitly-labeled supplier fixture (tests/fixtures/
//     printify-description.html, tests/fixtures/README.md) - already trusted via the explicit
//     "EU representative:" label path below; included here too so the SAME name is recognized
//     consistently if it ever appears via the narrative-sentence path for some other product.
// This allowlist exists ONLY to gate the narrative-sentence extraction path (see
// tryParseNarrativeGpsrStatement in pod-spec-parser.js) - it has no effect on, and is never
// consulted for, an explicit "EU representative: ..." label line, which was already a
// structurally unambiguous, trusted source before this task and remains so unconditionally.
const KNOWN_VERIFIED_EU_REPRESENTATIVES = ['SINDEN VENTURES LIMITED', 'HONSON VENTURES LIMITED'];

// GPSR production safety task (legal-data correctness fix): confirmed live (oversized-faded-
// t-shirt's real description) that a POD narrative GPSR sentence can pair a legitimate, verified
// EU representative with a DIFFERENT, unverified/placeholder "manufacturer" value in the very same
// sentence ("Oak inc." paired with the textbook placeholder address "123 Main Street, Anytown,
// Country"). This utility catches well-known template artifacts so any such value is rejected
// regardless of which field it was found in - never customer-facing without verification. Deliberately
// narrow (exact known placeholder tokens, not a broad heuristic) so it never
// false-positives on a real, unusual-but-genuine address.
const PLACEHOLDER_PATTERNS = [
  /\banytown\b/i,
  /123 main street/i,
  /\byour\s+(company|name|address|business)\b/i,
  /\[[^\]]*\]/, // bracketed template tokens, e.g. "[Company Name]"
  /\bexample\b/i,
  /\blorem\s+ipsum\b/i,
  /^n\/a$/i,
  /\btbd\b/i,
  /,\s*country\s*$/i, // a trailing literal "Country" with no real country name
];

/**
 * @param {string|null|undefined} value
 * @returns {boolean} true when the value matches a well-known placeholder/template pattern.
 */
function isLikelyPlaceholder(value) {
  if (!value) return false;
  return PLACEHOLDER_PATTERNS.some((re) => re.test(value));
}

/**
 * Owner addition (PDP safety and compliance task): structures the parser's raw GPSR label/value
 * pairs (pod-spec-parser.js's own generic, provider-agnostic `productSafety`) into the EU GPSR
 * field taxonomy this section displays. Pure relabeling/regrouping of already-parsed, already-
 * verified supplier text for ONE product's own fields only - a field this product's own supplier
 * text never provided stays absent (null) here too, never filled from another product or invented.
 * Exported (no DOM dependency) so this mapping can be unit-tested directly in Node, independent of
 * the browser-only rendering below.
 *
 * GPSR production safety task (legal-data correctness fix): now also accepts narrative-sentence
 * candidates (pod-spec-parser.js's `unverifiedGpsrCandidates`) as a FALLBACK EU-representative
 * source, used only when no explicit "EU representative:" label exists AND the candidate's name
 * matches KNOWN_VERIFIED_EU_REPRESENTATIVES above - "extract legal roles only when unambiguous."
 * The narrative path's manufacturer candidate is never consulted here at all, under any condition -
 * manufacturer identity (a merchant-specific fact, not fixed provider boilerplate) now comes
 * exclusively from the merchant-editable metafield source of truth composed in
 * snippets/pod-specifications.liquid, threaded in via the `metafieldManufacturer` parameter below.
 * @param {{label:string, value:string}[]} rawFields
 * @param {{manufacturerCandidate:string, euRepresentativeCandidate:string}[]} [unverifiedCandidates]
 * @param {string|null} [metafieldManufacturer] - pre-resolved "name, address, email" string from
 *   the merchant-editable metafield source of truth (product override, else shop default), or
 *   null when neither is set. Never a parsed/guessed value.
 */
export function buildGpsrFields(rawFields, unverifiedCandidates, metafieldManufacturer) {
  const fields = rawFields || [];
  const byLabel = (label) => fields.find((f) => f.label.toLowerCase() === label);

  const productInfo = byLabel('product information');
  let productIdentification = null;
  if (productInfo) {
    // Owner correction (PDP tab frames redesign task): strip a superseded EU warranty directive
    // clause (1999/44/EC) sometimes bundled into this same field - never shown as current legal
    // info (unchanged from the previous implementation).
    productIdentification = productInfo.value.replace(/,?\s*\d+\s*year warranty.*$/i, '').trim();
  }

  // GPSR's "manufacturer" and "EU responsible person" are distinct legal roles - never the same
  // raw field. Priority: the merchant-editable metafield source of truth (metafieldManufacturer -
  // a deliberate, verified merchant entry, see snippets/pod-specifications.liquid) first, since
  // that is now the primary intended source; an explicit, structurally-unambiguous supplier
  // "Manufacturer:" label line (pre-existing, unchanged behavior) as a fallback when no metafield
  // is set. Never the narrative-sentence candidate (pod-spec-parser.js's unverifiedGpsrCandidates)
  // under any condition - "never infer... manufacturer... records from company names alone."
  const manufacturerField = byLabel('manufacturer');
  const manufacturer =
    (metafieldManufacturer && metafieldManufacturer.trim()) ||
    (manufacturerField ? manufacturerField.value.trim() : null) ||
    null;

  // GPSR production safety task (legal-data correctness fix): an explicit "EU representative:"
  // label line is always structurally unambiguous and stays unconditionally trusted, exactly as
  // before. Only when that's absent, fall back to a narrative-sentence candidate (see
  // tryParseNarrativeGpsrStatement in pod-spec-parser.js) - and only when its name matches
  // KNOWN_VERIFIED_EU_REPRESENTATIVES above AND the full value doesn't match a known placeholder
  // pattern. An unverified/unmatched narrative candidate is never silently shown - it is simply
  // absent here (the caller's own console.warn for "no verified EU responsible person" already
  // covers this case identically to the pre-existing missing-field path).
  const euRepresentative = byLabel('eu representative');
  let euResponsiblePerson = euRepresentative ? reorderEuResponsiblePerson(euRepresentative.value) : null;
  if (!euResponsiblePerson && Array.isArray(unverifiedCandidates) && unverifiedCandidates.length > 0) {
    for (const candidate of unverifiedCandidates) {
      const candidateValue = candidate && candidate.euRepresentativeCandidate;
      if (!candidateValue) continue;
      const isKnownEntity = KNOWN_VERIFIED_EU_REPRESENTATIVES.some((name) =>
        candidateValue.toUpperCase().includes(name)
      );
      if (isKnownEntity && !isLikelyPlaceholder(candidateValue)) {
        euResponsiblePerson = reorderEuResponsiblePerson(candidateValue);
        break;
      }
    }
  }

  // Owner addition (PDP safety and compliance restructure task): shown only when a supplier's own
  // text explicitly states an adult-only restriction - never guessed, never defaulted, never shown
  // for the mere presence of an "age restriction"/"age rating" label whose value doesn't actually
  // say so. No current fixture or live product provides this field at all (confirmed by direct
  // inspection) - correctly inactive everywhere today, wired for whenever a supplier does provide
  // it.
  const ageField = byLabel('age restriction') || byLabel('age rating');
  const ageRestrictionApplies = Boolean(ageField && /adult/i.test(ageField.value));

  // "Warnings, Hazard" is the supplier's own raw label for what is often actually a benign sourcing
  // note (e.g. "Blank product sourced from Honduras", confirmed live) rather than a real hazard
  // warning - distinguished structurally (does the value actually state where the product was
  // sourced from?), not by supplier/provider identity. The whole sentence is kept verbatim as the
  // country-of-origin value either way - never trimmed down to just the country name, which would
  // require guessing exactly where the origin reference starts across different suppliers' own
  // phrasing (this task's own "never invent" instruction).
  const warningsField = byLabel('warnings, hazard') || byLabel('warnings') || byLabel('hazard');
  const explicitOrigin = byLabel('country of origin');
  let countryOfOrigin = explicitOrigin ? explicitOrigin.value.trim() : null;
  let safetyWarnings = null;
  if (warningsField) {
    if (/\bsourced from\b/i.test(warningsField.value)) {
      if (!countryOfOrigin) countryOfOrigin = warningsField.value.trim();
    } else {
      safetyWarnings = warningsField.value.trim();
    }
  }

  return {
    productIdentification,
    manufacturer,
    countryOfOrigin,
    euResponsiblePerson,
    ageRestrictionApplies,
    safetyWarnings,
  };
}

/** Owner addition (PDP safety and compliance restructure task): reorders a raw "EU representative"
 * value into the task's own required display order, regardless of what order the supplier's own
 * text happened to list them in. Content is never altered or invented, only regrouped: the email
 * is found by pattern match, the first remaining comma-separated segment is the name, everything
 * else remaining is the address, kept verbatim and in full. Falls back to the untouched original
 * value when no email is found at all (never silently drops real supplier text just because it
 * doesn't match the expected shape).
 *
 * Owner correction (PDP safety-and-compliance inline redesign task): order changed from "name,
 * email, address" to "name, address, email" - "[POD name, address and functional email link]",
 * this task's own explicit instruction, superseding the previous task's order.
 * @param {string} value @returns {string} */
function reorderEuResponsiblePerson(value) {
  const trimmed = value.trim();
  const emailMatch = trimmed.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
  if (!emailMatch) return trimmed;

  const email = emailMatch[0];
  const withoutEmail = (trimmed.slice(0, emailMatch.index) + trimmed.slice(emailMatch.index + email.length)).trim();
  const parts = withoutEmail
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const name = parts[0] || '';
  const address = parts.slice(1).join(', ');

  return [name, address, email].filter(Boolean).join(', ');
}

/** Owner addition (PDP safety-and-compliance inline redesign task): one inline "label: value ·
 * label: value · ..." paragraph - bold label (real <strong>, not CSS font-weight on plain text, so
 * copy/paste and assistive tech both see the real structure), value in the component's normal
 * typography, a light-gray " · " separator (.pod-specs__safety-sep, snippets/pod-specifications.
 * liquid) between items only (never leading/trailing - "no dangling separators"). An item whose
 * value contains a raw email (the EU responsible person) is auto-linked by appendTextWithEmailLinks
 * below, same as the rest of this file's supplier-sourced contact data.
 *
 * Owner correction (PDP safety-and-compliance layout fix task): root cause of the reported
 * horizontal overflow - `white-space: nowrap` previously applied to the WHOLE item (label AND
 * value together), forcing the entire EU responsible person field (name + full postal address +
 * email, confirmed live at 227px wider than its own 356px-wide panel on a 390px mobile viewport)
 * onto one unbreakable line. Fixed at the source instead of stacking an overflow/max-width
 * override: `nowrap` now applies ONLY to a short "· label:" unit (.pod-specs__safety-label-unit,
 * same stylesheet) - small enough to never itself overflow, and small enough that a separator can
 * never be stranded alone when it wraps (it always carries its own label with it). The value that
 * follows is plain breakable inline content with no nowrap of its own, so it wraps normally across
 * as many lines as it needs; .pod-specs__safety-field's own existing `overflow-wrap: anywhere`
 * still backstops any single unbroken run (e.g. a long email) that has no natural space to wrap at.
 * @param {Element} body @param {{label:string, value:string}[]} items */
function appendInlineFieldsParagraph(body, items) {
  const p = document.createElement('p');
  p.className = 'pod-specs__safety-field';
  items.forEach((item, i) => {
    const labelUnit = document.createElement('span');
    labelUnit.className = 'pod-specs__safety-label-unit';
    if (i > 0) {
      const sep = document.createElement('span');
      sep.className = 'pod-specs__safety-sep';
      sep.setAttribute('aria-hidden', 'true');
      sep.textContent = '·';
      labelUnit.appendChild(sep);
      labelUnit.appendChild(document.createTextNode(' '));
    }
    const strong = document.createElement('strong');
    strong.textContent = `${item.label}:`;
    labelUnit.appendChild(strong);
    p.appendChild(labelUnit);
    p.appendChild(document.createTextNode(' '));

    const valueSpan = document.createElement('span');
    valueSpan.className = 'pod-specs__safety-value';
    appendTextWithEmailLinks(valueSpan, item.value);
    p.appendChild(valueSpan);

    if (i < items.length - 1) {
      p.appendChild(document.createTextNode(' '));
    }
  });
  body.appendChild(p);
}

/** Owner addition (PDP safety and compliance restructure task): the fixed, global GPSR statement +
 * safety-contact sentence - a plain prose paragraph (no "label:" prefix, unlike paragraph 1 above),
 * with its one embedded email shown as its own real literal address (not the generic "email" word
 * appendTextWithEmailLinks uses for supplier-sourced contact data). Exact approved copy, never
 * reworded. @param {Element} body */
function appendGpsrStatementParagraph(body) {
  const p = document.createElement('p');
  p.className = 'pod-specs__safety-field';
  p.appendChild(document.createTextNode(GPSR_STATEMENT_PREFIX));
  const link = document.createElement('a');
  link.className = 'pod-specs__safety-email-link';
  link.href = `mailto:${PRODUCT_SAFETY_CONTACT_EMAIL}`;
  link.textContent = PRODUCT_SAFETY_CONTACT_EMAIL;
  p.appendChild(link);
  p.appendChild(document.createTextNode(GPSR_STATEMENT_SUFFIX));
  body.appendChild(p);
}

/** Owner correction (PDP tabs finalization task): "standard full-width text flow, one label and
 * its value within the same paragraph" - replaces the previous dl/dt/dd two-column list (no
 * multi-column safety layout, per this task's own explicit instruction).
 *
 * Owner correction (PDP safety-and-compliance inline redesign task): now exactly TWO paragraphs -
 * one inline "label: value · label: value" paragraph for every verified per-product GPSR field,
 * then the fixed global GPSR statement paragraph. "Store operator" is no longer rendered here at
 * all (STORE_OPERATOR's own data below is kept, per this task's own explicit "do not delete its
 * stored data", just not displayed in this subsection anymore). A field with no verified data
 * ("product identification"/"country of origin"/"eu responsible person" - the only three that were
 * previously flagged as "not provided...") is now silently omitted instead of shown as a flagged
 * sentence - this task's own explicit "Only render fields supported by verified product data. No
 * fabricated values or dangling separators" instruction leaves no room for a flag sentence inside a
 * compact inline list. A console.warn takes its place for whoever inspects devtools (the owner's
 * own QA), so a genuine compliance gap for a given product is still surfaced somewhere, just never
 * to the customer-facing page itself.
 * @param {{label:string, value:string}[]} fields
 * @param {{manufacturerCandidate:string, euRepresentativeCandidate:string}[]} [unverifiedCandidates]
 * @param {string|null} [metafieldManufacturer] */
function appendSafetySection(root, fields, unverifiedCandidates, metafieldManufacturer) {
  const section = document.createElement('div');
  section.className = 'pod-specs__safety pod-specs__section';
  appendSectionHeading(section, 'safety and compliance');
  const body = appendFrameBody(section);

  const gpsr = buildGpsrFields(fields, unverifiedCandidates, metafieldManufacturer);

  const items = [{ label: 'eu legal guarantee', value: EU_LEGAL_GUARANTEE_TEXT }];

  if (gpsr.productIdentification) {
    items.push({ label: 'product identification', value: gpsr.productIdentification });
  } else {
    console.warn('[pod-specifications] GPSR: no verified product identification for this product - omitted from the compact field list.');
  }

  if (gpsr.countryOfOrigin) {
    items.push({ label: 'country of origin', value: gpsr.countryOfOrigin });
  } else {
    console.warn('[pod-specifications] GPSR: no verified country of origin for this product - omitted from the compact field list.');
  }

  if (gpsr.manufacturer) {
    items.push({ label: 'manufacturer', value: gpsr.manufacturer });
  } else {
    // GPSR production safety task (legal-data correctness fix): manufacturer is "where
    // available" under GPSR (unlike EU responsible person, not universally mandatory for every
    // product), so this intentionally stays a console.warn, same tier as the other optional
    // fields above - never a customer-facing flag, but now surfaced for pre-launch QA (see
    // scripts/gpsr-qa-report.mjs) now that there's an actual resolution path (the merchant-
    // editable metafield source of truth) for a merchant to act on.
    console.warn('[pod-specifications] GPSR: no verified manufacturer for this product - set product.metafields.custom.manufacturer_name_override or shop.metafields.custom.manufacturer_name, omitted from the compact field list.');
  }

  if (gpsr.safetyWarnings) {
    items.push({ label: 'applicable product safety warnings', value: gpsr.safetyWarnings });
  }

  if (gpsr.ageRestrictionApplies) {
    items.push({ label: 'age restrictions', value: 'for adults' });
  }

  if (gpsr.euResponsiblePerson) {
    items.push({ label: 'eu responsible person', value: gpsr.euResponsiblePerson });
  } else {
    console.warn('[pod-specifications] GPSR: no verified EU responsible person for this product - required under the EU General Product Safety Regulation, omitted from the compact field list.');
  }

  appendInlineFieldsParagraph(body, items);
  appendGpsrStatementParagraph(body);

  root.appendChild(section);
}

/** Owner addition (PDP cosmetic update task): any email address inside a safety field's text (e.g.
 * the EU representative field's own "gpsr@honsonventures.com") becomes a real, clickable
 * mailto: link - generic pattern match, never a hardcoded address, so this works the same way for
 * whatever compliance email a given product's own supplier data actually contains. Built with
 * createTextNode/createElement only (never innerHTML) - this file's own established safety
 * convention for supplier-sourced text (see the class doc comment above).
 *
 * Owner correction (PDP GPSR email label task): the link's own visible text is now the generic
 * word "email" - "replace the visible text gpsr@honsonventures.com with email" - rather than the
 * raw address, while href still points at the real mailto:<address> destination (preserved
 * exactly, unchanged). Generic for the same reason the match itself is generic - any future
 * supplier email in this field reads the same simple "email" label, not a one-off hardcoded
 * substitution for this one address.
 * @param {Element} container @param {string} text */
function appendTextWithEmailLinks(container, text) {
  const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
  let lastIndex = 0;
  let match;
  while ((match = EMAIL_RE.exec(text))) {
    if (match.index > lastIndex) {
      container.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
    }
    const email = match[0];
    const link = document.createElement('a');
    link.className = 'pod-specs__safety-email-link';
    link.href = `mailto:${email}`;
    link.textContent = 'email';
    container.appendChild(link);
    lastIndex = match.index + email.length;
  }
  if (lastIndex < text.length) {
    container.appendChild(document.createTextNode(text.slice(lastIndex)));
  }
}

/**
 * Minimal translation lookup via the theme's own existing global dictionary
 * (window.Theme.translations, snippets/scripts.liquid - the same flat-key convention every other
 * JS-side string in this theme already uses, e.g. Theme.translations.cart_count), falling back to
 * the given English default when absent.
 * @param {string} key
 * @param {string} fallback
 */
function dict(key, fallback) {
  const translations = window.Theme && window.Theme.translations;
  return (translations && translations[key]) || fallback;
}

if (!customElements.get('pod-specifications')) {
  customElements.define('pod-specifications', PodSpecifications);
}
